import {
    Select,
    Box,
    Button,
    Flex,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    useBreakpointValue,    
    Badge,
    Spacer,
    Thead,
    Tr
} from "@chakra-ui/react";
import {
    FiX,
    FiSearch,
    FiChevronLeft,
    FiChevronRight,
    FiChevronsLeft,
    FiChevronsRight,
    FiArrowUp,
    FiArrowDown
} from "react-icons/fi";
import React, { useRef, useState, useMemo, useEffect } from "react";
import {
    getState,
    useAsyncDebounce,
    useBlockLayout,
    useExpanded,
    useFilters,
    useFlexLayout,
    useGlobalFilter,
    usePagination,
    useSortBy,
    useTable
} from "react-table";

export function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id }
}) {
  // Calculate the options for filtering
  // using the preFilteredRows
  const options = React.useMemo(() => {
    const options = new Set();
    preFilteredRows.forEach((row) => {
      options.add(row.values[id]);
    });
    return [...options.values()];
  }, [id, preFilteredRows]);

  return (
    <Select
      size={"xs"}
      value={filterValue}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
      variant={"outline"}
    >
      <option value="">All</option>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </Select>
  );
}

export function DataTable({ data, columns, subRowContents=false, initialState, customFilters=false, customTableSort=false, additionalData=false, searchEnabled=true }) {
    //don't render if we haven't gotten data yet
    const isMobile = useBreakpointValue(
        {
          "base": ()=> true,
          "xl": ()=>  false
        }
    );  

    data = useMemo(
        () => data,
        [data]
    );

    columns = useMemo(
        () => columns,
        [columns]
    );

    let tableInstance;

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        canPreviousPage,
        canNextPage,
        page,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, globalFilter, pageSize, filters, sortBy },
        preGlobalFilteredRows,
        setGlobalFilter,
        setSortBy,
        toggleAllRowsExpanded,
        toggleSortBy
    } = tableInstance = useTable(
        {
            columns,
            data,
            initialState: initialState
        },
        useFlexLayout,
        useFilters,
        useGlobalFilter,
        useSortBy,
        useExpanded,
        usePagination
    );

    useEffect(()=>{
      if (customFilters !== false) {
        const tableColumns = tableInstance.allColumns;

        //first clear everything
        tableColumns.forEach((tableColumn)=> {
          tableColumn?.setFilter(null);
        });

        const filterOptions = Object.entries(customFilters);
        if (filterOptions?.length > 0) {
          filterOptions.forEach(([columnId, filterValue]) => {
            tableColumns.every((tableColumn)=> {
              if (tableColumn && tableColumn?.id === columnId) {
                tableColumn?.setFilter(filterValue);

                return false;
              }
              return true;
            });
          });
        }
      } 
    },[customFilters]);

    useEffect(()=>{
      if (customTableSort !== false && customTableSort?.length > 0) {
        tableInstance.setSortBy(customTableSort);
      }
    },[customTableSort]);

    const pageArray = useMemo(()=>{
      const newPageArray = [];

      for (let i = 0; i < pageCount; i++) {
        newPageArray.push(i + 1);
      }

      return newPageArray;
    }, [[pageCount]]);

    
/*
    useEffect(()=>{
      console.log("when does this fire", sortBy)
      customTableSort.forEach((customSortItem)=>{
        if (customSortItem?.lockSort) {        
          sortBy.every((sortItem)=>{
            if (customSortItem?.id === sortItem?.id) {
              if (customSortItem.lockSort === "desc" && !sortItem?.desc) {
                tableInstance.toggleSortBy(customSortItem.id, true, false)
              }

              if (customSortItem.lockSort === "asc" && sortItem?.desc) {
                tableInstance.toggleSortBy(customSortItem.id, false, false)
              }
            }
          });
        }
      });
    },[sortBy]);
*/

  /* SEARCH */

  const inputRef = useRef();
  const [searchValue, setsearchValue] = useState(globalFilter);
  const onSearchInputChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  // const onSearchInputChange = (value)=>{
  //   setGlobalFilter(value || undefined);
  // }

  const PaginationRow = () => {
    return (
      <Flex maxWidth="550px" marginLeft="auto" align={"center"} justify={"end"} mt={"10px"} mb={"10px"} gap={"5px"}>
        <Flex alignItems="center" flexWrap="wrap"  flexDirection="row">
          <Text mr={1} fontSize={{base: "xs", xl: "sm"}}>
            {isMobile() ? "Results:" : "Results per page:"}
          </Text>
          <Select
            height="25px"
            bottom="2px"
            position="relative"
            cursor="pointer"
            fontSize="xs"
            mr={2}
            width="80px"
            value={pageSize}
            onChange={(selection)=>{
              setPageSize(parseInt(selection.target.value))
              gotoPage(0);
            }}
          >
            {data?.length >= 0 && (<option value="5">5</option>)}
            {data?.length >= 5 && (<option value="10">10</option>)}
            {data?.length >= 10 && (<option value="25">25</option>)}
            {data?.length >= 25 && (<option value="50">50</option>)}
            {data?.length >= 50 && (<option value="100">100</option>)}
            {data?.length >= 100 && (<option value="250">250</option>)}
            {data?.length >= 250 && (<option value="500">500</option>)}
            {data?.length >= 500 && (<option value="1000">1000</option>)}
            <option value="9999">All</option>
          </Select>
        </Flex>
        {pageCount > 1 && (
          <Flex alignItems="center" flexWrap="wrap" flexDirection="row">
            <Text mr={2} fontSize={{base: "xs", xl: "sm"}}>
                Page{" "}
                <strong>
                  {pageIndex + 1} of {pageOptions.length}
                </strong>{" "}
            </Text>
            <Flex flexDirection="row">
              <Button
                size={"xs"}
                onClick={() => gotoPage(0)}
                isDisabled={!canPreviousPage}
                mr={1}
              >
                <Icon boxSize={4} as={FiChevronsLeft} />
              </Button>{" "}
              <Button
                size={"xs"}
                onClick={() => previousPage()}
                isDisabled={!canPreviousPage}
                mr={1}
              >
                <Icon boxSize={4} as={FiChevronLeft} />
              </Button>{" "}
              {pageArray?.length > 0 && (
                <Select
                  fontSize="xs"
                  value={pageIndex + 1}
                  cursor="pointer"
                  height="25px"
                  width={pageCount < 99 ? "65px" : "70px"}
                  bottom="2px"
                  position="relative"
                  mr={1}
                  onChange={(selection)=>{
                    gotoPage(parseInt(selection.target.value - 1));
                  }}
                >
                  {pageArray.map((currentPage)=>(
                    <option value={currentPage}>{currentPage}</option>
                  ))}
                </Select>
              )}
              <Button
                size={"xs"}
                onClick={() => nextPage()}
                isDisabled={!canNextPage}
                mr={1}
              >
                <Icon boxSize={4} as={FiChevronRight} />
              </Button>{" "}
              <Button
                size={"xs"}
                onClick={() => gotoPage(pageCount - 1)}
                isDisabled={!canNextPage}
              >
                <Icon boxSize={4} as={FiChevronsRight} />
              </Button>{" "}
            </Flex>
          </Flex>
        )}
      </Flex>
    );
  }

  return (
    <>
      {searchEnabled && (
        <Box mb={"20px"}>
          <InputGroup>
            <InputLeftElement
              pointerEvents="none"
              children={<FiSearch color="gray.300" />}
            />
            <Input
              ref={inputRef}
              type="text"
              value={searchValue || ""}
              onChange={(e) => {
                setsearchValue(inputRef.current.value);
                onSearchInputChange(inputRef.current.value);
              }}
              placeholder={`Search...`}
            />
            {searchValue && (
              <InputRightElement
                cursor={"pointer"}
                children={
                  <FiX
                    fontSize={14}
                    _hover={{ color: "gray.600" }}
                    color="gray.300"
                  />
                }
                onClick={() => {
                  setGlobalFilter("");
                  setsearchValue("");
                }}
              />
            )}
          </InputGroup>
        </Box>
      )}

      <PaginationRow/>
      <TableContainer>
        <Table variant={"simple"} size="sm" {...getTableProps()}>
          <Thead>
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <>
                    {(!column.hasOwnProperty("show") || column?.show) && (
                      <Th key={column.id} width={isMobile() ? column.width : "inherit"} {...column.getHeaderProps(column.getSortByToggleProps())}>
                        <Flex align={"center"} gap={"1px"}>
                          <Box as="span" ml="auto" mr="auto"> {column.render("Header")} </Box>
                          {column.isSorted && (
                            <Box as="span">
                              {column.isSortedDesc ? (
                                <FiArrowDown boxSize={3} ml="1px"/>
                              ) : (
                                <FiArrowUp boxSize={3} ml="1px"/>
                              )}
                            </Box>
                          )}
                          <Box ml={2} as="span">
                            {column?.canFilter ? column.render("Filter") : null}
                          </Box>
                        </Flex>
                      </Th>
                    )}                  
                  </>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <>
                    <Tr key={row.id} {...row.getRowProps()} cursor={subRowContents ? "pointer" : "auto"} bgColor={row.isExpanded ? "whiteAlpha.100" : "inherit"} _hover={{ bgColor: "whiteAlpha.300"}}>
                    {row.cells.map((cell) => {
                        return (            
                          <>
                            {(!cell.column.hasOwnProperty("show") || cell.column?.show) && (
                              <Td 
                                textAlign="center"
                                width={isMobile() ? cell.column.width : "inherit"}
                                textOverflow="ellipsis"
                                overflow="hidden"
                                whiteSpace="nowrap"
                                key={cell.id} 
                                onClick={()=> {                                   
                                    if (subRowContents) {
                                        if (!row.isExpanded) {
                                            toggleAllRowsExpanded(false);
                                        }
                                        row.toggleRowExpanded();
                                    }
                                }} 
                                {...cell.getCellProps()}
                              >
                                  {cell.render("Cell")}
                              </Td>
                            )}
                          </>                              
                        );
                    })}
                    </Tr>
                    {row.isExpanded && subRowContents && (
                        <Tr>
                            <Td colSpan={row.cells.length} bgColor={"blackAlpha.900"}>{subRowContents(row.original, additionalData)}</Td>
                        </Tr>
                    )}
                </>
              );
            })}
          </Tbody>
        </Table>
        {/* <Box mt={4}>Showing the first 20 results of {rows.length} rows</Box> */}
        {data?.length > 10 && pageSize > 10 && (
          <PaginationRow/>
        )}
      </TableContainer>
    </>
  );
}

export function OriginalExample(props) {
  const data = useMemo(
    () => [
      {
        requestNo: "#1234",
        text: "Please disable this account",
        account: "Account 1",
        status: "Open"
      },
      {
        requestNo: "#1344",
        text: "Need top up",
        account: "Account 12",
        status: "Closed"
      },
      {
        requestNo: "#1234",
        text: "Please disable this account",
        account: "Account 1",
        status: "Open"
      },
      {
        requestNo: "#1234",
        text: "Please disable this account",
        account: "Account 1",
        status: "Open"
      },
      {
        requestNo: "#1344",
        text: "Need top up",
        account: "Account 12",
        status: "Closed"
      },
      {
        requestNo: "#1234",
        text: "Please disable this account",
        account: "Account 1",
        status: "Open"
      },
      {
        requestNo: "#1234",
        text: "Please disable this account",
        account: "Account 1",
        status: "Open"
      },
      {
        requestNo: "#1344",
        text: "Need top up",
        account: "Account 12",
        status: "Closed"
      },
      {
        requestNo: "#1234",
        text: "Please disable this account",
        account: "Account 1",
        status: "Open"
      },
      {
        requestNo: "#1234",
        text: "Please disable this account",
        account: "Account 1",
        status: "Open"
      },
      {
        requestNo: "#1344",
        text: "Need top up",
        account: "Account 12",
        status: "Closed"
      },
      {
        requestNo: "#1234",
        text: "Please disable this account",
        account: "Account 1",
        status: "Open"
      },
      {
        requestNo: "#1234",
        text: "Please disable this account",
        account: "Account 1",
        status: "Open"
      },
      {
        requestNo: "#1344",
        text: "Need top up",
        account: "Account 12",
        status: "Closed"
      },
      {
        requestNo: "#1234",
        text: "Please disable this account",
        account: "Account 1",
        status: "Open"
      }
    ],
    []
  );

  const columns = useMemo(
    () => [
      {
        Header: "Reqest No.",
        accessor: "requestNo",
        Filter: "",
        filter: ""
      },
      {
        Header: "Text",
        accessor: "text",
        Filter: "",
        filter: ""
      },
      {
        Header: "Account",
        accessor: "account",
        Filter: "",
        filter: ""
      },
      {
        Header: "Status",
        accessor: "status",
        //Filter: SelectColumnFilter,
        filter: "",
        disableSortBy: true,
        Cell: ({ row: { original } }) => (
          <Badge
            size={"xs"}
            colorScheme={original.status == "Open" ? "green" : "gray"}
          >
            {original.status}
          </Badge>
        )
      },
      {
        Header: "",
        accessor: "action",
        Filter: "",
        filter: "",
        disableSortBy: true,
        Cell: ({ row: { original } }) => (
          <Button
            colorScheme="teal"
            size={"sm"}
            onClick={() => alert(JSON.stringify(original))}
          >
            Update
          </Button>
        )
      }
    ],
    []
  );

  return (
    <div className="App" style={{ padding: "20px" }}>
      <DataTable columns={columns} data={data} searchEnabled={true} />
    </div>
  );
}